import React from "react";
import { Box, Grid, Typography } from "@mui/material";

import MHButton from "../Common/Button/MHButton";
import MHLogo from "../../theme/icons/MHLogo";

const POLICY_URL = "https://nigeriacarepolicy.org/care-policy";

// Partner names sit inside headings; the global span rule would otherwise shrink them.
const Highlight = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="span"
    sx={{ color: "#EDD5B1", font: "inherit", letterSpacing: "inherit" }}
  >
    {children}
  </Box>
);

const Partnership = () => (
  <Box
    sx={{
      mt: { xs: 6, md: 8 },
      pt: { xs: 5, md: 7 },
      borderTop: "1px solid rgba(160, 189, 210, 0.3)",
    }}
  >
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: { xs: 2, md: 3 },
        backgroundColor: "#FFFFFF",
        px: { xs: 2.5, md: 3 },
        py: 1.5,
      }}
    >
      <Box
        component="img"
        src="https://mh-resources-production.s3.us-west-1.amazonaws.com/ncp-7a.png"
        alt="Federal Ministry of Women Affairs and Social Development logo"
        draggable={false}
        sx={{ width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}
      />
      <Box sx={{ width: "1px", height: 40, backgroundColor: "#D6D7E0" }} />
      <MHLogo style={{ width: "4.5rem", margin: 0 }} />
    </Box>

    <Typography
      variant="h3"
      fontSize={{ xs: "24px", sm: "30px", md: "36px" }}
      lineHeight={{ xs: "130%", md: "125%" }}
      letterSpacing="-0.04em"
      className="pt-8"
      sx={{ color: "#FFFFFF", maxWidth: 940 }}
    >
      The{" "}
      <Highlight>
        Federal Ministry of Women Affairs and Social Development
      </Highlight>{" "}
      leads the process, convening the other ministries and carrying the policy
      through to adoption, with <Highlight>Caring Africa</Highlight> as
      technical partner drafting the framework.
    </Typography>

    <Typography
      variant="body1"
      fontSize={{ xs: "15px", md: "16px" }}
      lineHeight="180%"
      letterSpacing="0.03em"
      className="pt-6"
      sx={{ color: "#C7D8E4", maxWidth: 640 }}
    >
      Caring Africa brings a decade of operating experience and a network of
      over 10,000 verified caregivers and care centres.
    </Typography>
  </Box>
);

const NigeriaCarePolicy = () => (
  <Box
    component="section"
    sx={{ backgroundColor: "#194049", py: { xs: 10, md: 14 }, px: 2 }}
  >
    <Box sx={{ maxWidth: 1100, mx: "auto" }}>
      <Grid container spacing={{ xs: 3, md: 8 }} alignItems="flex-end">
        <Grid item xs={12} md={7}>
          <Typography
            variant="caption"
            fontSize={{ xs: "12px", sm: "14px" }}
            letterSpacing="0.1em"
            className="uppercase"
            sx={{ color: "#A0BDD2" }}
          >
            Policy in action
          </Typography>
          <Typography
            variant="h3"
            fontSize={{ xs: "38px", sm: "48px", md: "60px" }}
            lineHeight={{ xs: "105%", md: "98%" }}
            letterSpacing="-0.06em"
            className="pt-5"
            sx={{ color: "#FFFFFF" }}
          >
            Shaping Nigeria's{" "}
            <Box
              component="span"
              sx={{
                color: "#EDD5B1",
                font: "inherit",
                letterSpacing: "inherit",
              }}
            >
              National Care Policy
            </Box>
          </Typography>
        </Grid>
        <Grid item xs={12} md={5}>
          <Typography
            variant="body1"
            fontSize={{ xs: "15px", md: "16px" }}
            lineHeight="185%"
            letterSpacing="0.03em"
            sx={{ color: "#C7D8E4" }}
          >
            Caring Africa is the technical partner to the Federal Ministry of
            Women Affairs and Social Development on Nigeria's first National
            Care Policy.
          </Typography>
        </Grid>
      </Grid>

      {/* Convening photo */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          mt: { xs: 6, md: 8 },
          "&:hover img": { transform: "scale(1.03)" },
        }}
      >
        <Box
          component="img"
          src="https://mh-resources-production.s3.us-west-1.amazonaws.com/ncp_group_pics.jpeg"
          alt="Stakeholders at the National Care Sector Development convening hosted by the Federal Ministry of Women Affairs and Social Development"
          draggable={false}
          sx={{
            width: "100%",
            height: { xs: 260, sm: 360, md: 500 },
            objectFit: "cover",
            objectPosition: "center 40%",
            display: "block",
            transition: "transform 0.8s ease-out",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(25, 64, 73, 0) 55%, rgba(25, 64, 73, 0.85) 100%)",
          }}
        />
        <Typography
          variant="caption"
          fontSize={{ xs: "11px", sm: "12px" }}
          letterSpacing="0.1em"
          className="uppercase"
          sx={{
            position: "absolute",
            left: { xs: 16, md: 32 },
            bottom: { xs: 16, md: 28 },
            color: "#FFFFFF",
          }}
        >
          The National Care Sector Development Workshop
        </Typography>
      </Box>

      <Partnership />

      {/* Read the draft */}
      <Box
        sx={{
          backgroundColor: "#EDD5B1",
          mt: { xs: 6, md: 8 },
          p: { xs: 4, md: 6 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: { xs: 4, md: 8 },
        }}
      >
        <Box sx={{ maxWidth: 620 }}>
          <Typography
            variant="h3"
            color="primary"
            fontSize={{ xs: "30px", md: "38px" }}
            lineHeight="110%"
            letterSpacing="-0.05em"
          >
            Read The Draft Nigeria Care Policy
          </Typography>
          <Typography
            variant="body1"
            color="primary"
            fontSize={{ xs: "14px", md: "15px" }}
            lineHeight="180%"
            letterSpacing="0.03em"
            className="pt-4"
          >
            The Nigeria National Care Policy draft, developed by the Federal
            Ministry of Women Affairs, Caring Africa and the Nigeria Care
            Coalition, is open for public review. Preview it, download a copy,
            or leave a comment to inform the final draft.
          </Typography>
        </Box>

        <MHButton
          href={POLICY_URL}
          target="_blank"
          sx={{ minWidth: 220, flexShrink: 0 }}
        >
          Read the draft policy &rarr;
        </MHButton>
      </Box>
    </Box>
  </Box>
);

export default NigeriaCarePolicy;
